/**
 *  Copyright (c) 2026 TechDivision GmbH
 *  All rights reserved
 *
 *  This product includes proprietary software developed at TechDivision GmbH, Germany
 *  For more information see https://www.techdivision.com/
 *
 *  To obtain a valid license for using this software please contact us at
 *  license@techdivision.com
 *
 */

/* service-stations.js
   Service stations finder – Google Maps + list, client-side filtering and a
   rough haversine distance sort. Data is fetched from a JSON source configured
   on the section (bundled theme snapshot during development, App Proxy in
   production). Source of the snapshot:
   https://docs.knott.de/jsondata/response-service.json

   The source wraps the stations in an OData "value" array. Each station's
   ServicePartnerType is an array of { code, type } objects (see parseTypeCodes).

   Google Maps (Maps JavaScript API + Places API) is loaded lazily on init,
   behind the 2-click consent gate in initConsentGate().

   Filters: the category/service dropdowns are rendered from the
   `service_filter` metaobjects. Each <option> carries its ERP codes in a
   data-codes attribute, so filtering is generic — there is no hardcoded
   code mapping in this file anymore. A station matches a selected option when
   it has at least one of the option's codes. */

import { PIN_COLOR, PIN_COLOR_ACTIVE, pinIcon, buildClusterRenderer } from './service-stations-icons.js';
import { getConsentState, requestConsent, openConsentSettings, onConsentChange, GRANTED, NO_CMP } from './cmp-consent.js';

// feather-style contact icons (viewBox 0 0 24 24, stroke = currentColor), to
// match the theme's icon snippets
var ICON_PHONE =
  '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
  '<path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.9.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z"/>' +
  '</svg>';
var ICON_MAIL =
  '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
  '<rect x="2" y="4" width="20" height="16" rx="2"/>' +
  '<path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>' +
  '</svg>';

// shared promise so the Google Maps script is only injected once per page
var mapsLoaderPromise = null;

function loadGoogleMaps(apiKey) {
  if (window.google && window.google.maps && window.google.maps.places) {
    return Promise.resolve(window.google.maps);
  }
  if (mapsLoaderPromise) return mapsLoaderPromise;

  mapsLoaderPromise = new Promise(function (resolve, reject) {
    var callbackName = '__ssMapsReady';
    window[callbackName] = function () {
      resolve(window.google.maps);
      try { delete window[callbackName]; } catch (e) { window[callbackName] = undefined; }
    };

    var script = document.createElement('script');
    var params =
      'key=' + encodeURIComponent(apiKey) +
      '&libraries=places' +
      '&loading=async' +
      '&callback=' + callbackName;
    script.src = 'https://maps.googleapis.com/maps/api/js?' + params;
    script.async = true;
    script.defer = true;
    script.onerror = function () { reject(new Error('Google Maps script failed to load')); };
    document.head.appendChild(script);
  });

  return mapsLoaderPromise;
}

// shared promise so the MarkerClusterer library is only injected once per page
var clustererLoaderPromise = null;

// load the official @googlemaps/markerclusterer UMD build (exposes
// window.markerClusterer). served from a local theme asset (same-origin); the
// url is passed in from the section via data-clusterer-url. loaded lazily like
// the maps script, so it can sit behind the same consent gate later.
function loadMarkerClusterer(url) {
  if (window.markerClusterer && window.markerClusterer.MarkerClusterer) {
    return Promise.resolve(window.markerClusterer);
  }
  if (clustererLoaderPromise) return clustererLoaderPromise;

  clustererLoaderPromise = new Promise(function (resolve, reject) {
    var script = document.createElement('script');
    script.src = url || 'https://unpkg.com/@googlemaps/markerclusterer@2.5.3/dist/index.min.js';
    script.async = true;
    script.onload = function () { resolve(window.markerClusterer); };
    script.onerror = function () { reject(new Error('MarkerClusterer script failed to load')); };
    document.head.appendChild(script);
  });

  return clustererLoaderPromise;
}

// extract the ERP codes from the ServicePartnerType field. the current API
// delivers an array of objects ([{ code, type }, ...]); we still accept the
// legacy comma-separated string as a fallback.
function parseTypeCodes(raw) {
  if (!raw) return [];
  if (Array.isArray(raw)) {
    return raw
      .map(function (entry) {
        if (entry && typeof entry === 'object') return String(entry.code || '').trim();
        return String(entry || '').trim();
      })
      .filter(function (c) { return c.length > 0; });
  }
  // legacy: space-padded, comma-separated string
  return String(raw)
    .split(',')
    .map(function (c) { return c.trim(); })
    .filter(function (c) { return c.length > 0; });
}

function hasValidCoords(station) {
  var lat = station.lat;
  var lng = station.lng;
  return (
    typeof lat === 'number' && typeof lng === 'number' &&
    !isNaN(lat) && !isNaN(lng) &&
    // 0/0 is used in the source data as "no coordinates"
    !(lat === 0 && lng === 0)
  );
}

// rough great-circle distance in km (haversine); no routing API involved
function haversineKm(aLat, aLng, bLat, bLng) {
  var R = 6371;
  var dLat = (bLat - aLat) * Math.PI / 180;
  var dLng = (bLng - aLng) * Math.PI / 180;
  var lat1 = aLat * Math.PI / 180;
  var lat2 = bLat * Math.PI / 180;
  var h =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.sin(dLng / 2) * Math.sin(dLng / 2) * Math.cos(lat1) * Math.cos(lat2);
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
}

// normalise one raw JSON record into the shape the UI works with
function normaliseStation(raw, index) {
  var lat = parseFloat(raw.Latitude);
  var lng = parseFloat(raw.Longtitude); // note: source misspells "Longitude"
  var street = [raw.Street, raw.ZIP, raw.City]
    .filter(function (p) { return p && String(p).trim().length > 0; })
    .join(' ');

  return {
    id: index,
    name: (raw.Name || '').trim(),
    street: (raw.Street || '').trim(),
    zip: (raw.ZIP || '').trim(),
    city: (raw.City || '').trim(),
    country: (raw.Country || '').trim(),
    addressLine: street,
    phone: (raw.PhoneNumber || '').trim(),
    email: (raw.EmailAdress || '').trim(),
    website: (raw.Website || '').trim(),
    lat: isNaN(lat) ? null : lat,
    lng: isNaN(lng) ? null : lng,
    codes: parseTypeCodes(raw.ServicePartnerType)
  };
}

// a station matches a selected filter when it carries at least one of the
// filter's codes. categoryCodes / serviceCodes come from the chosen <option>'s
// data-codes attribute (empty array = "all", no restriction).
function matchesFilters(station, categoryCodes, serviceCodes) {
  if (categoryCodes && categoryCodes.length) {
    var catHit = categoryCodes.some(function (c) { return station.codes.indexOf(c) !== -1; });
    if (!catHit) return false;
  }
  if (serviceCodes && serviceCodes.length) {
    var svcHit = serviceCodes.some(function (c) { return station.codes.indexOf(c) !== -1; });
    if (!svcHit) return false;
  }
  return true;
}

function buildMapsRouteUrl(station) {
  var dest = station.addressLine || (station.lat + ',' + station.lng);
  return 'https://www.google.com/maps/dir/?api=1&destination=' + encodeURIComponent(dest);
}

export function initServiceStations() {
  var roots = document.querySelectorAll('[data-service-stations]');
  if (!roots.length) return;

  roots.forEach(function (root) {
    setupFinder(root);
  });
}

function setupFinder(root) {
  var dataUrl = root.getAttribute('data-data-url');
  var apiKey = root.getAttribute('data-maps-api-key');
  var centerLat = parseFloat(root.getAttribute('data-map-lat')) || 51.1657;
  var centerLng = parseFloat(root.getAttribute('data-map-lng')) || 10.4515;
  var defaultZoom = parseInt(root.getAttribute('data-map-zoom'), 10) || 6;
  var nearestCount = parseInt(root.getAttribute('data-nearest-count'), 10) || 10;
  var clustererUrl = root.getAttribute('data-clusterer-url');
  // which key identifies the map embed depends on the CMP the store runs:
  // Cookiebot knows only categories, Usercentrics only service ids. Both are
  // handed over and cmp-consent.js uses the one its detected CMP understands.
  // Neither set = no gate at all, the map loads straight away.
  var consentKeys = {
    category: root.getAttribute('data-consent-category') || '',
    serviceId: root.getAttribute('data-consent-service-id') || ''
  };
  var consentConfigured = !!(consentKeys.category || consentKeys.serviceId);

  var listEl = root.querySelector('[data-ss-list]');
  var resultsEl = root.querySelector('[data-ss-results]');
  var emptyEl = root.querySelector('[data-ss-empty]');
  var loadingEl = root.querySelector('[data-ss-loading]');
  var errorEl = root.querySelector('[data-ss-error]');
  var mapEl = root.querySelector('[data-ss-map]');
  var searchInput = root.querySelector('[data-ss-search]');
  var locateBtn = root.querySelector('[data-ss-locate]');
  var categorySelect = root.querySelector('[data-ss-filter-category]');
  var serviceSelect = root.querySelector('[data-ss-filter-service]');
  var mobileToggleButtons = Array.prototype.slice.call(root.querySelectorAll('[data-ss-mobile-toggle]'));
  var collapseBtn = root.querySelector('[data-ss-collapse]');
  var consentEl = root.querySelector('[data-ss-consent]');
  var consentAcceptBtn = root.querySelector('[data-ss-consent-accept]');
  var consentSettingsBtn = root.querySelector('[data-ss-consent-settings]');

  var state = {
    stations: [],
    filtered: [],
    origin: null, // { lat, lng } once an address/geolocation focus is set
    categoryCodes: [], // ERP codes of the selected category option ([] = all)
    serviceCodes: [], // ERP codes of the selected service option ([] = all)
    map: null,
    markers: {}, // id -> google.maps.Marker
    clusterer: null, // markerClusterer.MarkerClusterer instance
    infoWindow: null,
    geocoder: null, // google.maps.Geocoder, created on first Enter search
    activeId: null,
    activeMarkerId: null
  };

  // the list is rendered from the start (alphabetically, all matches); an
  // origin only switches it to a distance sort limited to the nearest N and
  // retires the search hint. data-ss-has-origin carries that state into the CSS
  function setOrigin(origin) {
    state.origin = origin;
    if (origin) {
      root.setAttribute('data-ss-has-origin', '');
      // a new origin re-sorts the list by distance; start it at the top so the
      // nearest station is in view (after applyFilters has re-rendered it)
      window.requestAnimationFrame(function () {
        if (resultsEl) resultsEl.scrollTop = 0;
      });
    } else {
      root.removeAttribute('data-ss-has-origin');
    }
  }

  // mobile list/map toggle: only one of the two is visible at a time (like VW)
  function setMobileView(view) {
    root.setAttribute('data-ss-mobile-view', view);
    mobileToggleButtons.forEach(function (btn) {
      btn.setAttribute('aria-pressed', btn.getAttribute('data-ss-mobile-toggle') === view ? 'true' : 'false');
    });
    // the map has no size while hidden; nudge it once it becomes visible
    if (view === 'map' && state.map && window.google) {
      window.google.maps.event.trigger(state.map, 'resize');
      // a previously selected station takes precedence: re-centre on its pin
      // and keep it highlighted (the resize can otherwise leave it off-centre)
      if (state.activeId != null && state.markers[state.activeId]) {
        var activeMarker = state.markers[state.activeId];
        setActiveMarker(state.activeId);
        state.map.setCenter(activeMarker.getPosition());
        var station = findStation(state.activeId);
        if (station) openInfo(station, activeMarker);
      } else if (state.origin) {
        focusNearest();
      } else {
        state.map.setCenter({ lat: centerLat, lng: centerLng });
      }
    }
    // when switching back to the list, jump to the previously selected entry
    // (scrollIntoView only works once the list is visible again)
    if (view === 'list') {
      window.requestAnimationFrame(scrollActiveCardIntoView);
    }
  }
  mobileToggleButtons.forEach(function (btn) {
    btn.addEventListener('click', function () {
      setMobileView(btn.getAttribute('data-ss-mobile-toggle'));
    });
  });

  // desktop: collapse the panel out to the left and back. the data-ss-collapsed
  // flag drives the CSS transform; the button's arrow flips via aria-expanded.
  if (collapseBtn) {
    collapseBtn.addEventListener('click', function () {
      var collapsed = root.hasAttribute('data-ss-collapsed');
      if (collapsed) {
        root.removeAttribute('data-ss-collapsed');
        collapseBtn.setAttribute('aria-expanded', 'true');
      } else {
        root.setAttribute('data-ss-collapsed', '');
        collapseBtn.setAttribute('aria-expanded', 'false');
      }
      // the map viewport width effectively changes; keep it rendered correctly
      if (state.map && window.google) {
        window.google.maps.event.trigger(state.map, 'resize');
      }
    });
  }

  // parse a comma-separated data-codes attribute into a trimmed code array
  function parseCodesAttr(value) {
    if (!value) return [];
    return value.split(',').map(function (c) { return c.trim(); }).filter(function (c) { return c.length > 0; });
  }

  // read the codes of the currently selected option of a <select>
  function selectedCodes(select) {
    if (!select) return [];
    var opt = select.options[select.selectedIndex];
    return opt ? parseCodesAttr(opt.getAttribute('data-codes')) : [];
  }

  // collect every filter option (label + its codes) from both dropdowns, so the
  // attribute chips can reuse the exact same translated labels the customer
  // maintains in the metaobjects. skips the "all" option (no data-codes).
  function collectFilterOptions(select) {
    var out = [];
    if (!select) return out;
    Array.prototype.slice.call(select.options).forEach(function (opt) {
      var codes = parseCodesAttr(opt.getAttribute('data-codes'));
      if (codes.length) out.push({ label: opt.textContent.trim(), codes: codes });
    });
    return out;
  }
  var filterOptions = collectFilterOptions(categorySelect).concat(collectFilterOptions(serviceSelect));

  // the attributes that apply to a station: every filter option whose codes the
  // station carries, as the customer-maintained (translated) labels
  function stationAttributes(station) {
    var chips = [];
    filterOptions.forEach(function (opt) {
      var hit = opt.codes.some(function (c) { return station.codes.indexOf(c) !== -1; });
      if (hit) chips.push(opt.label);
    });
    return chips;
  }

  if (categorySelect) {
    categorySelect.addEventListener('change', function () {
      state.categoryCodes = selectedCodes(categorySelect);
      applyFilters();
    });
  }
  if (serviceSelect) {
    serviceSelect.addEventListener('change', function () {
      state.serviceCodes = selectedCodes(serviceSelect);
      applyFilters();
    });
  }

  if (locateBtn) {
    locateBtn.addEventListener('click', function () {
      if (!navigator.geolocation) return;
      locateBtn.disabled = true;
      navigator.geolocation.getCurrentPosition(
        function (pos) {
          setOrigin({ lat: pos.coords.latitude, lng: pos.coords.longitude });
          locateBtn.disabled = false;
          applyFilters();
        },
        function () {
          locateBtn.disabled = false;
        }
      );
    });
  }

  // kick off data + maps in parallel; render as soon as both are ready
  var stationsReady = fetchStations(dataUrl)
    .then(function (raw) {
      state.stations = raw.map(normaliseStation);
    });

  // the station data comes from Knott's own API, the map does not: only the
  // Google embed needs consent. So the list and its filters are built either
  // way and just the map waits behind the gate — that is what makes this a
  // two-click solution rather than an all-or-nothing block.
  stationsReady
    .then(function () {
      if (loadingEl) loadingEl.hidden = true;
      applyFilters();
    })
    .catch(function (e) {
      if (loadingEl) loadingEl.hidden = true;
      if (errorEl) errorEl.hidden = false;
      console.error('Service Stations data error:', e);
    });

  initConsentGate();

  // guards against a second run: consent can be granted while the script is
  // still loading, and the CMP fires its change event on every save
  var mapStarted = false;

  function startMap() {
    if (mapStarted || !apiKey || !mapEl) return Promise.resolve(false);
    mapStarted = true;

    return loadGoogleMaps(apiKey)
      .then(function () {
        if (!window.google) return false;
        // the placeholder sits inside the map container; Google takes that
        // element over, so it has to be gone before the map is built
        removeConsentGate();
        initMap();
        initAutocomplete();
        // stations may already be on screen when consent arrives late
        renderMarkers();
        if (state.origin && isMapVisible()) focusNearest();
        // clustering is a progressive enhancement: load it in the background
        // and re-render markers once it is ready. failure just leaves the
        // individual pins in place.
        loadMarkerClusterer(clustererUrl)
          .then(function () { renderMarkers(); })
          .catch(function (e) { console.warn('Service Stations clustering unavailable:', e); });
        return true;
      })
      .catch(function (e) {
        mapStarted = false;
        console.warn('Service Stations map unavailable:', e);
        return false;
      });
  }

  // with nothing configured there is no embed to gate, so the finder behaves
  // exactly as it did before the gate existed
  function initConsentGate() {
    if (!consentConfigured) {
      startMap();
      return;
    }

    showConsentGate();

    getConsentState(consentKeys).then(handleState);

    // someone can still accept (or revoke) in the CMP window afterwards
    onConsentChange(consentKeys, handleState);

    if (consentAcceptBtn) {
      consentAcceptBtn.addEventListener('click', function () {
        consentAcceptBtn.disabled = true;
        requestConsent(consentKeys)
          .then(function (granted) {
            if (granted) return startMap();
            return null;
          })
          .then(function () {
            consentAcceptBtn.disabled = false;
          });
      });
    }

    if (consentSettingsBtn) {
      consentSettingsBtn.addEventListener('click', function () {
        openConsentSettings();
      });
    }
  }

  // NO_CMP is not a refusal: it means no consent manager is on this page at
  // all, so there is nothing that could ever grant the map and nothing that
  // records a decision either. Blocking it forever would break the finder on
  // every environment without a CMP, so the map loads and says so in the
  // console. A CMP that merely boots slowly does not land here — it gets the
  // full deadline in cmp-consent.js first.
  function handleState(state) {
    if (state === GRANTED) {
      startMap();
      return;
    }
    if (state === NO_CMP) {
      console.warn(
        'Service Stations: no consent manager found, the map is loaded ungated'
      );
      startMap();
    }
  }

  // address search and geolocation both run through the Google Places API, so
  // they are as gated as the map itself
  function showConsentGate() {
    if (consentEl) consentEl.hidden = false;
    root.setAttribute('data-ss-consent-blocked', '');
    if (searchInput) searchInput.disabled = true;
    if (locateBtn) locateBtn.disabled = true;
  }

  function removeConsentGate() {
    if (consentEl && consentEl.parentNode) consentEl.parentNode.removeChild(consentEl);
    root.removeAttribute('data-ss-consent-blocked');
    if (searchInput) searchInput.disabled = false;
    if (locateBtn) locateBtn.disabled = false;
  }

  function fetchStations(url) {
    return fetch(url, { headers: { Accept: 'application/json' } })
      .then(function (res) {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.json();
      })
      .then(function (json) {
        // the source wraps the list in an OData "value" array
        if (json && Array.isArray(json.value)) return json.value;
        if (Array.isArray(json)) return json;
        return [];
      });
  }

  function initMap() {
    // 'cooperative' on every viewport: the wheel scrolls the page and only
    // Cmd/Ctrl + wheel zooms, so a full-bleed map cannot swallow the scroll and
    // trap the visitor. touch keeps two-finger pan/zoom. Google renders the
    // localised hint overlay for both cases itself
    state.map = new window.google.maps.Map(mapEl, {
      center: { lat: centerLat, lng: centerLng },
      zoom: defaultZoom,
      gestureHandling: 'cooperative',
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false
    });
    state.infoWindow = new window.google.maps.InfoWindow();
  }

  function initAutocomplete() {
    if (!searchInput || !window.google.maps.places) return;
    var autocomplete = new window.google.maps.places.Autocomplete(searchInput, {
      fields: ['geometry'],
      types: ['geocode']
    });

    // track whether the current input matches a picked suggestion, so Enter
    // does not needlessly geocode when a place was already selected
    var picked = false;

    autocomplete.addListener('place_changed', function () {
      var place = autocomplete.getPlace();
      if (place && place.geometry && place.geometry.location) {
        picked = true;
        focusOn(place.geometry.location.lat(), place.geometry.location.lng());
      }
    });

    // Enter without choosing a suggestion: geocode the typed text directly.
    // if a suggestion is highlighted, Google fires place_changed first; the
    // short delay lets that happen and we only geocode the raw query as a
    // fallback when no place was picked.
    searchInput.addEventListener('keydown', function (e) {
      if (e.key !== 'Enter') return;
      e.preventDefault(); // avoid submitting any surrounding form
      picked = false;
      setTimeout(function () {
        if (picked) return; // a suggestion was chosen -> already handled
        var query = searchInput.value.trim();
        if (query) geocodeAndFocus(query);
      }, 250);
    });
  }

  // resolve a free-text address to coordinates and focus the map/list on it
  function geocodeAndFocus(query) {
    if (!window.google || !window.google.maps) return;
    if (!state.geocoder) state.geocoder = new window.google.maps.Geocoder();
    state.geocoder.geocode({ address: query }, function (results, status) {
      if (status === 'OK' && results && results[0] && results[0].geometry) {
        var loc = results[0].geometry.location;
        focusOn(loc.lat(), loc.lng());
      }
    });
  }

  // shared: set the origin from coordinates and refresh
  function focusOn(lat, lng) {
    setOrigin({ lat: lat, lng: lng });
    applyFilters();
  }

  function applyFilters() {
    var matches = state.stations.filter(function (s) {
      return matchesFilters(s, state.categoryCodes, state.serviceCodes);
    });

    // distance-sort when we have an origin; entries without coords go last
    if (state.origin) {
      matches.forEach(function (s) {
        s._distance = hasValidCoords(s)
          ? haversineKm(state.origin.lat, state.origin.lng, s.lat, s.lng)
          : Infinity;
      });
      matches.sort(function (a, b) { return a._distance - b._distance; });
    } else {
      matches.sort(function (a, b) { return a.name.localeCompare(b.name); });
    }

    state.filtered = matches;
    // the list is only shown once an origin exists; before that we still render
    // all matching markers on the map, just no list
    renderList();
    renderMarkers();

    // focus map on the nearest N when an origin is set – but only while the map
    // actually has a size. on mobile the map is hidden in the list view, where
    // fitBounds would compute a wrong (fully zoomed-out) viewport; there the
    // focus is deferred to setMobileView('map') when switching to the map.
    if (state.origin && state.map && window.google && isMapVisible()) {
      focusNearest();
    }
  }

  // the map element has no layout size while hidden (mobile list view)
  function isMapVisible() {
    return !!(mapEl && mapEl.offsetWidth > 0 && mapEl.offsetHeight > 0);
  }

  // the visible list is limited to the nearest N once an origin is set
  function visibleStations() {
    if (state.origin) return state.filtered.slice(0, nearestCount);
    return state.filtered;
  }

  function renderList() {
    if (!listEl) return;
    listEl.innerHTML = '';

    var visible = visibleStations();
    if (emptyEl) emptyEl.hidden = visible.length !== 0;

    var frag = document.createDocumentFragment();
    visible.forEach(function (station) {
      frag.appendChild(renderCard(station));
    });
    listEl.appendChild(frag);
  }

  function renderCard(station) {
    var li = document.createElement('li');
    li.className = 'service-station-card';
    li.setAttribute('data-ss-item', String(station.id));
    if (!hasValidCoords(station)) li.setAttribute('data-ss-no-pin', '');

    // 1) name
    var name = document.createElement('h3');
    name.className = 'service-station-card__name';
    name.textContent = station.name;
    li.appendChild(name);

    // 2) matching filter attributes as chips
    var attributes = stationAttributes(station);
    if (attributes.length) {
      var attrs = document.createElement('div');
      attrs.className = 'service-station-card__attributes';
      attributes.forEach(function (label) {
        var chip = document.createElement('span');
        chip.className = 'service-station-card__chip';
        chip.textContent = label;
        attrs.appendChild(chip);
      });
      li.appendChild(attrs);
    }

    // 3) divider
    li.appendChild(cardDivider());

    // 4) address with distance on the left, route link below
    var addressBlock = document.createElement('div');
    addressBlock.className = 'service-station-card__address-block';

    if (station.addressLine) {
      var addr = document.createElement('p');
      addr.className = 'service-station-card__address';

      // address on the left; distance pushed to the right edge via margin-left
      // auto (so it must come AFTER the address text in the DOM)
      var addrText = document.createElement('span');
      addrText.className = 'service-station-card__address-text';
      addrText.textContent = station.addressLine;
      addr.appendChild(addrText);

      // distance shown to the right, only when an origin is set and the station
      // has valid coordinates
      if (state.origin && hasValidCoords(station) && typeof station._distance === 'number' && isFinite(station._distance)) {
        var distance = document.createElement('span');
        distance.className = 'service-station-card__distance';
        distance.textContent = formatDistance(station._distance);
        addr.appendChild(distance);
      }

      addressBlock.appendChild(addr);
    }

    var route = document.createElement('a');
    route.className = 'service-station-card__route';
    route.href = buildMapsRouteUrl(station);
    route.target = '_blank';
    route.rel = 'noopener';
    route.textContent = root.getAttribute('data-route-label') || 'Route';
    addressBlock.appendChild(route);

    li.appendChild(addressBlock);

    // 5) contact details (phone / email)
    if (station.phone || station.email) {
      li.appendChild(cardDivider());

      var contact = document.createElement('div');
      contact.className = 'service-station-card__contact';

      if (station.phone) {
        contact.appendChild(
          contactLink('tel:' + station.phone.replace(/[^\d+]/g, ''), station.phone, ICON_PHONE)
        );
      }
      if (station.email) {
        contact.appendChild(
          contactLink('mailto:' + station.email, station.email, ICON_MAIL)
        );
      }
      li.appendChild(contact);
    }

    // clicking a list item focuses its pin
    li.addEventListener('click', function (e) {
      if (e.target.closest('a')) return; // let links behave normally
      focusStation(station.id);
    });

    return li;
  }

  function renderMarkers() {
    if (!state.map || !window.google) return;

    var clusterable = window.markerClusterer && window.markerClusterer.MarkerClusterer;

    // tear down existing markers and any previous clusterer
    if (state.clusterer) {
      state.clusterer.clearMarkers();
    }
    Object.keys(state.markers).forEach(function (id) {
      state.markers[id].setMap(null);
    });
    state.markers = {};
    state.activeMarkerId = null;

    var markerList = [];
    state.filtered.forEach(function (station) {
      if (!hasValidCoords(station)) return; // no pin for missing coordinates
      var marker = new window.google.maps.Marker({
        // when clustering, the clusterer manages map membership, so don't set
        // `map` here; otherwise attach the marker directly
        map: clusterable ? null : state.map,
        position: { lat: station.lat, lng: station.lng },
        title: station.name,
        icon: pinIcon(window.google, PIN_COLOR, 32)
      });
      marker.addListener('click', function () {
        // clicking a single pin selects the station in the list beside the map
        openInfo(station, marker);
        highlightCard(station.id);
        setActiveMarker(station.id);
        // centre the map on the selected pin
        if (state.map) state.map.panTo(marker.getPosition());
        // on mobile the list and map are separate views, so switch to the list
        if (window.matchMedia && window.matchMedia('(max-width: 767px)').matches) {
          setMobileView('list');
        }
      });
      state.markers[station.id] = marker;
      markerList.push(marker);
    });

    // group nearby pins into clusters (created once, reused afterwards)
    if (clusterable) {
      if (!state.clusterer) {
        state.clusterer = new window.markerClusterer.MarkerClusterer({
          map: state.map,
          markers: markerList,
          renderer: buildClusterRenderer(window.google),
          // custom click handler so the zoom-in behaves identically on desktop
          // and mobile. the default handler animates a fitBounds, whose
          // duration depends on the zoom delta / viewport size, so it feels
          // slow on mobile. we jump straight to the cluster instead.
          onClusterClick: function (event, cluster, map) {
            zoomToCluster(cluster);
          }
        });
      } else {
        state.clusterer.addMarkers(markerList);
      }
    }
  }

  // jump to a cluster without the smooth (device-dependent) fitBounds
  // animation: centre on the cluster and step the zoom in immediately.
  // clusters whose markers all share (nearly) the same coordinates can never
  // split apart by zooming, so in that case we reveal them in the list instead.
  function zoomToCluster(cluster) {
    if (!state.map || !cluster) return;
    var markers = cluster.markers || [];
    var current = state.map.getZoom() || defaultZoom;

    // unresolvable when all markers sit on the same spot, or we are already
    // at max zoom and the cluster still holds more than one marker
    if (markers.length > 1 && (markersShareLocation(markers) || current >= 16)) {
      revealClusterInList(markers);
      return;
    }

    var position = cluster.position || (cluster.bounds && cluster.bounds.getCenter());
    if (position) state.map.setCenter(position);
    state.map.setZoom(Math.min(current + 3, 16));
  }

  // true when every marker in the list is at essentially the same coordinate
  function markersShareLocation(markers) {
    if (markers.length < 2) return false;
    var first = markers[0].getPosition();
    if (!first) return false;
    return markers.every(function (m) {
      var p = m.getPosition();
      return p &&
        Math.abs(p.lat() - first.lat()) < 1e-4 &&
        Math.abs(p.lng() - first.lng()) < 1e-4;
    });
  }

  // select the first station of an unresolvable cluster in the list
  function revealClusterInList(markers) {
    // centre the map on the shared spot and zoom in as far as sensible
    var pos = markers[0].getPosition();
    if (pos) {
      state.map.setCenter(pos);
      state.map.setZoom(Math.min((state.map.getZoom() || defaultZoom) + 3, 16));
    }
    // map the cluster's markers back to station ids and highlight the first
    var ids = markers
      .map(function (m) { return markerStationId(m); })
      .filter(function (id) { return id != null; });
    if (ids.length) {
      highlightCard(ids[0]);
      setActiveMarker(ids[0]);
      if (window.matchMedia && window.matchMedia('(max-width: 767px)').matches) {
        setMobileView('list');
      }
    }
  }

  // reverse lookup: google.maps.Marker -> station id
  function markerStationId(marker) {
    var ids = Object.keys(state.markers);
    for (var i = 0; i < ids.length; i++) {
      if (state.markers[ids[i]] === marker) return parseInt(ids[i], 10);
    }
    return null;
  }

  // highlight one marker (larger, darker orange) and reset the previous one
  function setActiveMarker(id) {
    if (!window.google) return;
    if (state.activeMarkerId != null && state.markers[state.activeMarkerId]) {
      state.markers[state.activeMarkerId].setIcon(pinIcon(window.google, PIN_COLOR, 32));
      state.markers[state.activeMarkerId].setZIndex(1);
    }
    var marker = state.markers[id];
    if (marker) {
      marker.setIcon(pinIcon(window.google, PIN_COLOR_ACTIVE, 42));
      marker.setZIndex(999);
      state.activeMarkerId = id;
    }
  }

  function openInfo(station, marker) {
    if (!state.infoWindow) return;
    var html =
      '<div class="ss-infowindow"><strong>' + escapeHtml(station.name) + '</strong><br>' +
      escapeHtml(station.addressLine) + '</div>';
    state.infoWindow.setContent(html);
    // cap the width so the bubble hugs its content instead of Google's default
    state.infoWindow.setOptions({ maxWidth: 260 });
    state.infoWindow.open(state.map, marker);
  }

  // list -> map: center the pin and open its info window
  function focusStation(id) {
    highlightCard(id);
    var marker = state.markers[id];
    if (marker && state.map) {
      // mark active + centre before any view switch, so switching to the map
      // view finds a complete active state
      setActiveMarker(id);
      var station = findStation(id);
      if (station) openInfo(station, marker);
      state.map.panTo(marker.getPosition());
      if (state.map.getZoom() < 10) state.map.setZoom(11);
      // on mobile, switch to the map so the focused pin is actually visible
      if (window.matchMedia && window.matchMedia('(max-width: 767px)').matches) {
        setMobileView('map');
      }
    }
  }

  // map -> list: highlight the card and scroll it into view
  function highlightCard(id) {
    state.activeId = id;
    var items = listEl ? listEl.querySelectorAll('[data-ss-item]') : [];
    Array.prototype.forEach.call(items, function (item) {
      var isActive = item.getAttribute('data-ss-item') === String(id);
      item.classList.toggle('is-active', isActive);
    });
    scrollActiveCardIntoView();
  }

  // scroll the active card to the top of the visible list. only works while the
  // list is visible, so it is also called when switching to the list view on
  // mobile. we scroll the results container manually (rather than
  // scrollIntoView) so the card lands at the top and the page itself never
  // scrolls.
  function scrollActiveCardIntoView() {
    if (state.activeId == null || !listEl || !resultsEl) return;
    var item = listEl.querySelector('[data-ss-item="' + state.activeId + '"]');
    if (!item) return;
    // position of the card relative to the current scroll top of the container
    var containerRect = resultsEl.getBoundingClientRect();
    var itemRect = item.getBoundingClientRect();
    var top = resultsEl.scrollTop + (itemRect.top - containerRect.top);
    resultsEl.scrollTo({ top: top, behavior: 'smooth' });
  }

  function focusNearest() {
    var withCoords = state.filtered.filter(hasValidCoords).slice(0, nearestCount);
    if (!withCoords.length) {
      recenter();
      return;
    }
    var bounds = new window.google.maps.LatLngBounds();
    if (state.origin) bounds.extend(state.origin);
    withCoords.forEach(function (s) {
      bounds.extend({ lat: s.lat, lng: s.lng });
    });
    state.map.fitBounds(bounds);
  }

  function recenter() {
    if (state.map) state.map.setCenter({ lat: centerLat, lng: centerLng });
  }

  function findStation(id) {
    for (var i = 0; i < state.stations.length; i++) {
      if (state.stations[i].id === id) return state.stations[i];
    }
    return null;
  }
}

function escapeHtml(str) {
  return String(str || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

// human-friendly air-line distance label: "< 1 km", "3.4 km", "27 km"
function formatDistance(km) {
  if (km < 1) return '< 1 km';
  if (km < 10) return km.toFixed(1).replace('.', ',') + ' km';
  return Math.round(km) + ' km';
}

// horizontal divider element used to separate card sections
function cardDivider() {
  var hr = document.createElement('hr');
  hr.className = 'service-station-card__divider';
  return hr;
}

// a contact link (phone/email) prefixed with an inline SVG icon
function contactLink(href, text, iconSvg) {
  var a = document.createElement('a');
  a.className = 'service-station-card__link';
  a.href = href;

  var icon = document.createElement('span');
  icon.className = 'service-station-card__link-icon';
  icon.innerHTML = iconSvg;
  a.appendChild(icon);

  var label = document.createElement('span');
  label.textContent = text;
  a.appendChild(label);

  return a;
}

// loaded as a module script by the section that needs it (see
// snippets/module-script.liquid), so the module initialises itself. module
// scripts are deferred, which means the DOM is complete at this point
initServiceStations();
